import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';

import { DatePipe } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ShopSearchHelperService } from '../shop-search-helper.service';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { DatePickerService } from './date-picker.service';
import { ScrollService } from '../../../../core/services/scroll.service';
import { FOLD_OUT_ANIMATION } from '../../../../core/animations/FOLD_OUT_ANIMATION';
import { calendarEnd, calendarStart, IconComponent, provideIcons } from '../../../icons';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';

export enum DatePickerState {
  START,
  END,
  HIDE
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  animations: [FOLD_OUT_ANIMATION],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ calendarStart, calendarEnd }), DatePipe],
  imports: [ReactiveFormsModule, IconComponent, AlpitranslatePipe]
})
export class DatePickerComponent implements OnInit, OnDestroy {
  @Input() subFormGroup: FormGroup;

  /**
   * set the default startdate. has to be a date >= now
   */
  @Input() fromDate: { year: number; month: number; day: number }; //NgbDateStruct; // used to remember the fromDate in the datepicker selection

  /**
   * set the default enddate. has to be bigger than fromDate
   */
  @Input() toDate: { year: number; month: number; day: number }; // used to remember the toDate in the datepicker selection

  /**
   * event fired each time a new date range is selected
   */
  @Output() changedDates: EventEmitter<{ start: Date; end: Date }> = new EventEmitter<{
    start: Date;
    end: Date;
  }>();

  @ViewChild('daterangeform', { static: true }) daterangeform: ElementRef;
  @ViewChild('rangePicker', { static: true, read: ViewContainerRef })
  protected rangePickerAnchor: ViewContainerRef;

  get startDate() {
    return this.subFormGroup.controls.startDate;
  }
  get endDate() {
    return this.subFormGroup.controls.endDate;
  }
  get previousDateAsDate() {
    return new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day - 1);
  }

  // helper to know if focus is on picker elements
  focusStart = false;
  focusEnd = false;
  culture: string; // selected language

  readonly datePickerState = DatePickerState;
  _pickerState: DatePickerState = DatePickerState.HIDE;
  get pickerState() {
    return this._pickerState;
  }
  set pickerState(state) {
    this._pickerState = state;
    if (this._pickerState !== this.datePickerState.HIDE) {
      this.initRangePicker();
    } else {
      this.removeRangePicker();
    }
  }
  hoveredElement = 0;

  protected onSizeReferenceChangeSubscription: Subscription;

  private format = 'EEEEEE dd.MM.yy';
  private picker;

  constructor(
    private datePipe: DatePipe,
    public translateService: TranslateService,
    protected resolutionService: ResolutionService,
    protected shopSearchHelperService: ShopSearchHelperService,
    private changeDetectorRef: ChangeDetectorRef,
    private datePickerService: DatePickerService,
    private scrollService: ScrollService
  ) {}

  ngOnInit() {
    // set datetimepicker to default range
    if (this.fromDate === undefined) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.fromDate = {
        year: tomorrow.getFullYear(),
        month: tomorrow.getMonth() + 1,
        day: tomorrow.getDate()
      };
    }
    if (this.toDate === undefined) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 4);
      this.toDate = { year: tomorrow.getFullYear(), month: tomorrow.getMonth() + 1, day: tomorrow.getDate() };
    }

    this.culture = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe((lang: LangChangeEvent) => {
      this.culture = lang.lang;
      this.translateService.get('ALPINSKIHIRE.shopSearch.datePickerDisplayFormat').subscribe(translation => {
        this.format = translation;
        this.setStartDate(this.fromDate);
        this.setEndDate(this.toDate);
      });
      this.changeDetectorRef.detectChanges();
    });

    this.translateService.get('ALPINSKIHIRE.shopSearch.datePickerDisplayFormat').subscribe(translation => {
      this.format = translation;
    });

    // set the start date
    if (this.fromDate) {
      this.setStartDate(this.fromDate);
    }

    // set the end date
    if (this.toDate) {
      this.setEndDate(this.toDate);
    }
  }

  ngOnDestroy() {
    if (this.onSizeReferenceChangeSubscription) {
      this.onSizeReferenceChangeSubscription.unsubscribe();
    }
  }

  protected async initRangePicker() {
    const { RangepickerComponent } = await import('../../rangepicker/rangepicker.component');
    if (this.rangePickerAnchor.length < 1) {
      this.rangePickerAnchor.clear();
      this.picker = this.rangePickerAnchor.createComponent(RangepickerComponent);
      this.picker.setInput('initWith', this.pickerState);
      this.picker.setInput('fromDate', {
        year: this.fromDate.year,
        month: this.fromDate.month,
        day: this.fromDate.day
      });
      this.picker.setInput('toDate', {
        year: this.toDate.year,
        month: this.toDate.month,
        day: this.toDate.day
      });
      this.picker.setInput('ignoreClicksOn', this.rangePickerAnchor.element);
      this.picker.instance.hoveredStart.subscribe(ev => this.hoverUpdateStart(ev));
      this.picker.instance.hoveredEnd.subscribe(ev => this.hoverUpdateEnd(ev));
      this.picker.instance.selectedRange.subscribe(ev => this.onDateSelection(ev));
      this.picker.instance.clickOutside.subscribe(() => this.removeRangePicker());
    } else {
      this.picker.setInput('initWith', this.pickerState);
    }
  }

  protected removeRangePicker() {
    this.rangePickerAnchor.clear();
    this.setStartDate(this.fromDate);
    this.setEndDate(this.toDate);
    this.hoveredElement = 0;
    this.changeDetectorRef.detectChanges();
  }

  hoverUpdateStart($event: Date | null) {
    this.hoveredElement = 1;
    if (!$event) {
      this.startDate.setValue('');
    } else {
      this.setStartDate(
        { year: $event.getFullYear(), month: $event.getMonth() + 1, day: $event.getDate() },
        false
      );
    }
  }

  hoverUpdateEnd($event: Date | null) {
    this.hoveredElement = 2;
    if (!$event) {
      this.endDate.setValue('');
    } else {
      this.setEndDate(
        { year: $event.getFullYear(), month: $event.getMonth() + 1, day: $event.getDate() },
        false
      );
    }
  }

  /**
   * datetimepicker functionality
   * @param date the selected Date
   */
  onDateSelection(range: { start: Date; end: Date }) {
    this.fromDate = {
      year: range.start.getFullYear(),
      month: range.start.getMonth() + 1,
      day: range.start.getDate()
    };
    this.toDate = {
      year: range.end.getFullYear(),
      month: range.end.getMonth() + 1,
      day: range.end.getDate()
    };
    this.changedDates.emit({
      start: range.start,
      end: range.end
    });
    this.pickerState = DatePickerState.HIDE;
    this.hoveredElement = 0;
  }

  /**
   * set the forminput for the startdate to the given date. Will format and propagate a form change if you wish
   * @param date date you want to set the start date to
   * @param propagate propagate a form change or not. default true
   * @param format the date format you want to see in the form. has culture fallback
   */
  private setStartDate(
    date: { year: number; month: number; day: number },
    propagate = true,
    format?: string
  ) {
    if (propagate) {
      this.datePickerService.start = new Date(date.year, date.month - 1, date.day);
    }
    setTimeout(() => {
      this.startDate.setValue(this.transformDateToString(date, format));
    });
  }

  /**
   * set the forminput for the enddate to the given date. Will format and propagate a form change if you wish
   * @param date date you want to set the end date to
   * @param propagate propagate a form change or not. default true
   * @param format the date format you want to see in the form. has culture fallback
   */
  private setEndDate(date: { year: number; month: number; day: number }, propagate = true, format?: string) {
    if (propagate) {
      this.datePickerService.end = new Date(date.year, date.month - 1, date.day);
    }
    setTimeout(() => {
      this.endDate.setValue(this.transformDateToString(date, format));
    });
  }

  private transformDateToString(
    date: Date | { year: number; month: number; day: number },
    format: string
  ): string {
    if (format === undefined) {
      format = this.format;
    }
    return this.datePipe.transform(
      date instanceof Date ? date : new Date(date.year, date.month - 1, date.day),
      format,
      undefined,
      this.culture
    );
  }

  /**
   * allows you to set the datepicker as focus element, scrolls page if appropriate and will open the calendar afterwards
   */
  public setFocus(scrollIntoView?: ScrollIntoViewOptions) {
    if (scrollIntoView !== undefined) {
      this.scrollService.scrollIntoView(this.daterangeform.nativeElement);
    }
    setTimeout(() => {
      this.pickerState = DatePickerState.START;
      this.hoveredElement = 1;
    }, 500);
  }
}
